import { useClientTaskPreviews } from "@api/organizationTask.api";
import { useAddClientAction } from "@components/organizationView/clients/useAddClientAction";
import type { ClientTaskPreview } from "@common/schemas/organization-task.schema";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { Button } from "@design-system/Button/Button";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { ArrowCornerIcon, PlusIcon, TransactionsIcon } from "@icons";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { OrgClientRow } from "./OrgClientRow";
import type { SideNavSectionState } from "./sideNavSection.types";
import type { RecentClients } from "./useRecentClients";
import styles from "./OrgClientsSection.module.scss";

const ALL_CLIENTS_PATH = "/";

const AddClientButton = () => {
    const addClientAction = useAddClientAction();
    const isDisabled =
        addClientAction.isEnrollmentRequired && addClientAction.isDisabled;
    const disabledReason = addClientAction.isEnrollmentRequired
        ? addClientAction.disabledReason
        : undefined;

    return (
        <Tooltip content={disabledReason ?? "Add client"}>
            <Button
                variant="secondary"
                size="xs"
                iconOnly
                aria-label="Add client"
                onClick={addClientAction.addClient}
                disabled={isDisabled}
            >
                <PlusIcon />
            </Button>
        </Tooltip>
    );
};

const ViewAllClientsButton = () => (
    <Tooltip content="View all clients">
        <Button
            variant="secondary"
            size="xs"
            asChild
            aria-label="View all clients"
            className={styles.iconOnlyLink}
        >
            <StateAwareLink to={ALL_CLIENTS_PATH}>
                <ArrowCornerIcon />
            </StateAwareLink>
        </Button>
    </Tooltip>
);

const NO_TASKS: readonly ClientTaskPreview[] = [];

/**
 * The firm's most recent clients (see `useRecentClients`), each opening its
 * tasks (or switching into its workspace like the workspace picker does) and
 * expanding into its open tasks; then "All clients". The header's `+` runs
 * the add-client flow and its arrow opens the full client list.
 */
export const OrgClientsSection = ({
    section,
    recentClients,
    allClientsShortcut,
}: {
    section: SideNavSectionState;
    recentClients: RecentClients;
    allClientsShortcut?: string;
}) => {
    const canCreateClient = useCanPerformOrganizationAction(
        OrganizationAction.CREATE_CLIENT,
    );
    const { data: tasksByWorkspaceId } = useClientTaskPreviews({
        workspaceIds: recentClients.map(({ id }) => id),
    });

    return (
        <SideNavSection
            {...section}
            action={
                <>
                    {canCreateClient && <AddClientButton />}
                    <ViewAllClientsButton />
                </>
            }
        >
            {recentClients.map((workspace, index) => (
                <OrgClientRow
                    key={workspace.id}
                    workspace={workspace}
                    tasks={tasksByWorkspaceId?.get(workspace.id) ?? NO_TASKS}
                    isExpandedByDefault={index === 0}
                />
            ))}
            <SideNavItem
                to={ALL_CLIENTS_PATH}
                end
                icon={<TransactionsIcon />}
                label="All clients"
                shortcut={allClientsShortcut}
            />
        </SideNavSection>
    );
};
